import { useEffect, useRef, useState } from 'react';
import { Button, HStack, IconButton, Spinner, Text } from '@chakra-ui/react';
import { Pause, Play } from 'lucide-react';
import { useAtom } from 'jotai';
import { FILE_LIMITS } from '@twinden/shared';
import { Waveform } from './Waveform';
import { formatTime } from './messageUtils';
import { voiceSpeedAtom } from '../../store';
import {
  nextSpeed,
  rememberPosition,
  rememberedPosition,
  speedLabel,
} from '../../lib/voicePeaks';
import { useT } from '../../i18n';

// Older voice notes have no stored waveform: a flat bar (D221).
const FLAT_WAVEFORM = Array.from(
  { length: FILE_LIMITS.VOICE_WAVEFORM_BARS },
  () => 0
);

// Only one voice note plays at a time across the whole chat.
let playing: HTMLAudioElement | null = null;

interface VoiceNotePlayerProps {
  /** The message's id: where it was left is remembered by it (D221). */
  id?: string;
  src: string;
  /** Seconds, from the message metadata. */
  duration?: number;
  waveform?: number[];
  isOwn: boolean;
}

// Voice-note playback (issue #1, M60 D221). Length and waveform come from
// the message metadata, so nothing is downloaded until the user presses
// play. (Recorded WebM files also carry no duration header, so the browser
// could not show the length up front anyway.) A chip sets the speed for
// every voice note on this device; dragging along the waveform scrubs.
export function VoiceNotePlayer({
  id,
  src,
  duration,
  waveform,
  isOwn,
}: VoiceNotePlayerProps) {
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const t = useT();
  const [speed, setSpeed] = useAtom(voiceSpeedAtom);
  const speedRef = useRef(speed);
  speedRef.current = speed;
  const [state, setState] = useState<'idle' | 'loading' | 'playing' | 'paused'>(
    () => (id && rememberedPosition(id) > 0 ? 'paused' : 'idle')
  );
  const [position, setPosition] = useState(() =>
    id ? rememberedPosition(id) : 0
  );
  // Where a drag would land, shown while scrubbing.
  const [scrub, setScrub] = useState<number | null>(null);

  const total =
    duration && duration > 0
      ? duration
      : audioRef.current && Number.isFinite(audioRef.current.duration)
        ? audioRef.current.duration
        : 0;

  // The chosen speed applies to a note that's already playing too.
  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;
    audio.defaultPlaybackRate = speed;
    audio.playbackRate = speed;
  }, [speed]);

  // Stop and release the audio if the message scrolls away for good; where
  // it was is remembered for when it comes back.
  useEffect(
    () => () => {
      const audio = audioRef.current;
      if (!audio) return;
      if (id) rememberPosition(id, audio.ended ? 0 : audio.currentTime);
      audio.pause();
      if (playing === audio) playing = null;
      audio.removeAttribute('src');
      audio.load();
    },
    [id]
  );

  const ensureAudio = () => {
    if (audioRef.current) return audioRef.current;
    const audio = new Audio();
    audio.preload = 'none';
    audio.addEventListener('timeupdate', () => {
      setPosition(audio.currentTime);
      if (id) rememberPosition(id, audio.currentTime);
    });
    audio.addEventListener('playing', () => setState('playing'));
    audio.addEventListener('waiting', () => setState('loading'));
    audio.addEventListener('pause', () =>
      setState((current) => (current === 'idle' ? current : 'paused'))
    );
    audio.addEventListener('ended', () => {
      setState('idle');
      setPosition(0);
      if (id) rememberPosition(id, 0);
      if (playing === audio) playing = null;
    });
    audio.addEventListener('error', () => setState('idle'));
    // Speed is per device (D221); a new element starts at it.
    audio.addEventListener('loadedmetadata', () => {
      audio.playbackRate = speedRef.current;
    });
    // Downloaded on first play, not when the message renders.
    audio.src = src;
    audio.defaultPlaybackRate = speedRef.current;
    audio.playbackRate = speedRef.current;
    audioRef.current = audio;
    return audio;
  };

  const play = (from?: number) => {
    const audio = ensureAudio();
    if (playing && playing !== audio) playing.pause();
    playing = audio;
    const start = from ?? (audio.currentTime > 0 ? undefined : position);
    if (start !== undefined && start > 0) {
      try {
        audio.currentTime = start;
      } catch {
        // Not seekable yet; plays from the start instead.
      }
    }
    if (audio.paused) {
      setState('loading');
      void audio.play().catch(() => setState('idle'));
    }
  };

  const toggle = () => {
    const audio = audioRef.current;
    if (audio && !audio.paused) {
      audio.pause();
      return;
    }
    play();
  };

  const seek = (fraction: number) => {
    setScrub(null);
    if (!total) return;
    setPosition(fraction * total);
    if (id) rememberPosition(id, fraction * total);
    play(fraction * total);
  };

  const shownPosition = scrub !== null ? scrub * total : position;
  const progress = total ? Math.min(1, shownPosition / total) : 0;
  const active = state !== 'idle' || scrub !== null;

  return (
    // A white pill with a round play button (Living Room), whoever sent it.
    <HStack
      spacing="8px"
      h="52px"
      pl="4px"
      pr="8px"
      mt={1}
      w={{ base: '260px', md: '300px' }}
      maxW="full"
      borderRadius="full"
      bg="surface"
      borderWidth="1px"
      borderColor="line"
      data-own={isOwn || undefined}
    >
      <IconButton
        aria-label={t(
          state === 'playing' ? 'chat.voice.pause' : 'chat.voice.play'
        )}
        icon={
          state === 'loading' ? (
            <Spinner size="xs" />
          ) : state === 'playing' ? (
            <Pause size={16} fill="currentColor" />
          ) : (
            <Play size={16} fill="currentColor" />
          )
        }
        colorScheme="brand"
        flexShrink={0}
        onClick={toggle}
      />
      <Waveform
        bars={waveform && waveform.length > 0 ? waveform : FLAT_WAVEFORM}
        progress={progress}
        color="gray.300"
        playedColor="accent"
        height={24}
        onSeek={total ? seek : undefined}
        onScrub={total ? setScrub : undefined}
        label={t('chat.voice.position')}
      />
      <Text
        fontSize="13px"
        color="muted"
        flexShrink={0}
        minW="32px"
        textAlign="right"
        sx={{ fontVariantNumeric: 'tabular-nums' }}
      >
        {formatTime(active ? shownPosition : total)}
      </Text>
      <Button
        size="xs"
        h="26px"
        minW="38px"
        px={2}
        borderRadius="full"
        variant="outline"
        colorScheme="gray"
        borderColor="line"
        fontSize="12px"
        fontWeight={700}
        flexShrink={0}
        aria-label={t('chat.voice.speed', { speed: speedLabel(speed) })}
        data-testid="voice-speed"
        onClick={() => setSpeed(nextSpeed(speed))}
      >
        {speedLabel(speed)}
      </Button>
    </HStack>
  );
}
